import { useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from "react";
import type { DataSource, FieldSchema } from "@broadpaper/core";
import type { EditorView } from "prosemirror-view";
import { flattenSchemas, type PickerEntry } from "../inspector/DataPicker.js";
import { acceptToken, dismissAutocomplete, type AutocompleteHandlers, type TokenTrigger } from "./autocomplete.js";
import { Icon, FIELD_TYPE_ICONS } from "../ui/icons.js";

const MAX_ITEMS = 8;

/** Ranks a field against what has been typed after the braces. */
function score(entry: PickerEntry, query: string): number {
  if (!query) return 1;
  const expr = entry.expr.toLowerCase();
  const label = entry.label.toLowerCase();
  const leaf = entry.expr.slice(entry.expr.lastIndexOf(".") + 1).toLowerCase();
  // A prefix match on the name people would type beats a match buried in the
  // middle of a long path.
  if (leaf.startsWith(query)) return 100 - leaf.length;
  if (expr.startsWith(query)) return 90 - expr.length;
  if (leaf.includes(query)) return 60;
  if (expr.includes(query)) return 40;
  if (label.includes(query)) return 20;
  return 0;
}

export interface TokenSuggestionsProps {
  trigger: TokenTrigger;
  view: EditorView;
  dataSources: DataSource[];
  scopeVars?: Record<string, FieldSchema>;
  /** Position of the trigger, in the overlay's coordinate space. */
  at: { left: number; top: number };
  onClose: () => void;
}

/**
 * The list offered while a `{{` is open.
 *
 * It owns the highlighted row, and hands the editor a set of handlers so the
 * arrow keys and Enter reach the list before ProseMirror's own bindings.
 */
export const TokenSuggestions = forwardRef<AutocompleteHandlers, TokenSuggestionsProps>(function TokenSuggestions(
  { trigger, view, dataSources, scopeVars, at, onClose },
  ref
) {
  const [index, setIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const entries = useMemo(() => flattenSchemas(dataSources, scopeVars ?? {}), [dataSources, scopeVars]);
  const items = useMemo(() => {
    const q = trigger.query.toLowerCase();
    return entries
      .map((e) => ({ e, s: score(e, q) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, MAX_ITEMS)
      .map((x) => x.e);
  }, [entries, trigger.query]);

  // A new query starts from the top; keeping the old row would highlight
  // something the user never looked at.
  useEffect(() => setIndex(0), [trigger.query]);

  const accept = (i = index): boolean => {
    const item = items[i];
    if (!item) return false;
    acceptToken(view, trigger, item.expr, item.label);
    return true;
  };

  useImperativeHandle(
    ref,
    (): AutocompleteHandlers => ({
      isOpen: () => items.length > 0,
      move: (delta) => setIndex((i) => (items.length ? (i + delta + items.length) % items.length : 0)),
      accept: () => accept(),
      close: () => {
        dismissAutocomplete(view);
        onClose();
      }
    }),
    [items, index, view, trigger, onClose]
  );

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [index]);

  if (!items.length) return null;

  return (
    <div className="bpe-suggest" style={{ left: at.left, top: at.top }} role="listbox" aria-label="Insert a data field" ref={listRef} onPointerDown={(e) => e.preventDefault()}>
      {items.map((item, i) => (
        <div
          key={item.expr}
          role="option"
          aria-selected={i === index}
          className={`bpe-suggest__item ${i === index ? "bpe-suggest__item--active" : ""}`}
          onMouseEnter={() => setIndex(i)}
          onClick={() => accept(i)}
        >
          <Icon name={FIELD_TYPE_ICONS[item.type] ?? "circle"} size={13} className="bpe-suggest__icon" />
          <span className="bpe-suggest__label bpe-truncate">{item.label}</span>
          <span className="bpe-suggest__expr bpe-truncate">{item.expr}</span>
        </div>
      ))}
      <div className="bpe-suggest__hint">
        <kbd>↑</kbd>
        <kbd>↓</kbd> to choose · <kbd>Enter</kbd> to insert · <kbd>Esc</kbd> to dismiss
      </div>
    </div>
  );
});
